import { useAuth } from "./lib/auth-client";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { AppShell } from "./components/app-shell";
import { ProtectedFecDetailRoute } from "./components/protected-fec-detail-route";
import { createAccountApi } from "./lib/account-api";
import { api } from "./lib/api";
import { buildSignInRedirectPath } from "./lib/auth-routing";
import { useSeo } from "./lib/seo";

function lazyRoute<T extends ComponentType<any>>(load: () => Promise<T>) {
  return lazy(async () => ({ default: await load() }));
}

const AboutPage = lazyRoute(
  async () => (await import("./pages/about-page")).AboutPage,
);
const AccountSettingsPage = lazyRoute(
  async () =>
    (await import("./pages/account-settings-page")).AccountSettingsPage,
);
const BillDetailPage = lazyRoute(
  async () => (await import("./pages/bill-detail-page")).BillDetailPage,
);
const BillVoteDetailPage = lazyRoute(
  async () =>
    (await import("./pages/bill-vote-detail-page")).BillVoteDetailPage,
);
const BillsPage = lazyRoute(
  async () => (await import("./pages/bills-page")).BillsPage,
);
const CommitteeDetailPage = lazyRoute(
  async () =>
    (await import("./pages/committee-detail-page")).CommitteeDetailPage,
);
const ContactPage = lazyRoute(
  async () => (await import("./pages/contact-page")).ContactPage,
);
const CreateMonitorPage = lazyRoute(
  async () => (await import("./pages/create-monitor-page")).CreateMonitorPage,
);
const DesignSystemPreviewPage = lazyRoute(
  async () =>
    (await import("./pages/design-system-preview-page"))
      .DesignSystemPreviewPage,
);
const FecCommitteeDetailPage = lazyRoute(
  async () =>
    (await import("./pages/fec-committee-detail-page")).FecCommitteeDetailPage,
);
const ForgotPasswordPage = lazyRoute(
  async () => (await import("./pages/forgot-password-page")).ForgotPasswordPage,
);
const HomePage = lazyRoute(
  async () => (await import("./pages/home-page")).HomePage,
);
const HouseRollCallVoteDetailPage = lazyRoute(
  async () =>
    (await import("./pages/house-roll-call-vote-detail-page"))
      .HouseRollCallVoteDetailPage,
);
const InsightsPage = lazyRoute(
  async () => (await import("./pages/insights-page")).InsightsPage,
);
const AiLegislationInsightPage = lazyRoute(
  async () =>
    (await import("./pages/ai-legislation-insight-page"))
      .AiLegislationInsightPage,
);
const CongressExplainedPage = lazyRoute(
  async () =>
    (await import("./pages/congress-explained-page")).CongressExplainedPage,
);
const CongressExplainerPage = lazyRoute(
  async () =>
    (await import("./pages/congress-explainer-page")).CongressExplainerPage,
);
const HowABillBecomesLawPage = lazyRoute(
  async () =>
    (await import("./pages/how-a-bill-becomes-law-page"))
      .HowABillBecomesLawPage,
);
const ClotureInsightPage = lazyRoute(
  async () => (await import("./pages/cloture-insight-page")).ClotureInsightPage,
);
const ReintroducedBillsInsightPage = lazyRoute(
  async () =>
    (await import("./pages/reintroduced-bills-insight-page"))
      .ReintroducedBillsInsightPage,
);
const ReportedByCommitteeInsightPage = lazyRoute(
  async () =>
    (await import("./pages/reported-by-committee-insight-page"))
      .ReportedByCommitteeInsightPage,
);
const MoneyInfluencePage = lazyRoute(
  async () =>
    (await import("./pages/money-influence-page")).MoneyInfluencePage,
);
const OutsideSpendingInsightPage = lazyRoute(
  async () =>
    (await import("./pages/outside-spending-insight-page"))
      .OutsideSpendingInsightPage,
);
const PacVsSuperPacInsightPage = lazyRoute(
  async () =>
    (await import("./pages/pac-vs-super-pac-insight-page"))
      .PacVsSuperPacInsightPage,
);
const LegalPage = lazyRoute(
  async () => (await import("./pages/legal-page")).LegalPage,
);
const MeetingDetailPage = lazyRoute(
  async () => (await import("./pages/meeting-detail-page")).MeetingDetailPage,
);
const MemberDetailPage = lazyRoute(
  async () => (await import("./pages/member-detail-page")).MemberDetailPage,
);
const MembersPage = lazyRoute(
  async () => (await import("./pages/members-page")).MembersPage,
);
const MonitorPage = lazyRoute(
  async () => (await import("./pages/monitor-page")).MonitorPage,
);
const NotificationsSettingsPage = lazyRoute(
  async () =>
    (await import("./pages/notifications-settings-page"))
      .NotificationsSettingsPage,
);
const PricingPage = lazyRoute(
  async () => (await import("./pages/pricing-page")).PricingPage,
);
const PrivacyPolicyPage = lazyRoute(
  async () => (await import("./pages/privacy-policy-page")).PrivacyPolicyPage,
);
const ResetPasswordPage = lazyRoute(
  async () => (await import("./pages/reset-password-page")).ResetPasswordPage,
);
const SchedulePage = lazyRoute(
  async () => (await import("./pages/schedule-page")).SchedulePage,
);
const SenateFloorVoteDetailPage = lazyRoute(
  async () =>
    (await import("./pages/senate-floor-vote-detail-page"))
      .SenateFloorVoteDetailPage,
);
const SignInPage = lazyRoute(
  async () => (await import("./pages/sign-in-page")).SignInPage,
);
const SignUpPage = lazyRoute(
  async () => (await import("./pages/sign-up-page")).SignUpPage,
);
const TermsOfServicePage = lazyRoute(
  async () =>
    (await import("./pages/terms-of-service-page")).TermsOfServicePage,
);
const WelcomeAccessPage = lazyRoute(
  async () => (await import("./pages/welcome-access-page")).WelcomeAccessPage,
);
const WelcomePage = lazyRoute(
  async () => (await import("./pages/welcome-page")).WelcomePage,
);

function RouteLoadingFallback({
  label = "Loading page...",
}: {
  label?: string;
}) {
  return (
    <div className="app-route-loading" role="status" aria-live="polite">
      {label}
    </div>
  );
}

function ProtectedFecCommitteeDetailPage() {
  const { committeeId } = useParams();

  return (
    <ProtectedFecDetailRoute committeeId={committeeId}>
      <FecCommitteeDetailPage />
    </ProtectedFecDetailRoute>
  );
}

function ProtectedSettingsPage({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const redirectPath = `${location.pathname}${location.search}${location.hash}`;

  if (!isLoaded) {
    return <RouteLoadingFallback label="Loading account..." />;
  }

  if (!isSignedIn) {
    return <Navigate replace to={buildSignInRedirectPath(redirectPath)} />;
  }

  return children;
}

function NotFoundPage() {
  const location = useLocation();
  useSeo({ kind: "fallback", pathname: location.pathname });

  return (
    <div className="beta-monitor-page">
      <section className="card section-card">
        <h1>Page not found</h1>
        <p>The page you requested is not available.</p>
      </section>
    </div>
  );
}

function resolveOnboardingPath(pathname: string): string | null {
  if (pathname === "/welcome" || pathname.startsWith("/welcome/")) {
    return pathname.startsWith("/welcome/access")
      ? "/welcome/access"
      : "/welcome";
  }

  return null;
}

function isPublicMarketingRoute(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname === "/about" ||
    pathname === "/contact" ||
    pathname === "/insights" ||
    pathname.startsWith("/insights/") ||
    pathname === "/legal" ||
    pathname === "/pricing" ||
    pathname === "/privacy" ||
    pathname === "/forgot-password" ||
    pathname === "/sign-in" ||
    pathname.startsWith("/sign-in/") ||
    pathname === "/sign-up" ||
    pathname.startsWith("/sign-up/") ||
    pathname === "/reset-password" ||
    pathname.startsWith("/reset-password/") ||
    pathname === "/terms"
  );
}

function isDesignSystemPreviewRoute(pathname: string): boolean {
  return pathname === "/design-system";
}

function isDesignSystemPreviewEnabled(): boolean {
  return (
    import.meta.env.DEV ||
    import.meta.env.VITE_ENABLE_DESIGN_SYSTEM_PREVIEW === "true"
  );
}

function DesignSystemPreviewUnavailablePage() {
  return (
    <main className="min-h-svh bg-background p-8 text-foreground">
      <div className="mx-auto grid max-w-xl gap-3 rounded-lg border border-border bg-card p-6">
        <p className="text-sm font-medium text-muted-foreground">
          Design system preview unavailable
        </p>
        <h1 className="text-2xl font-semibold">
          The internal preview route is disabled.
        </h1>
        <p className="text-muted-foreground">
          Run in dev mode or set VITE_ENABLE_DESIGN_SYSTEM_PREVIEW=true to view
          this internal preview.
        </p>
      </div>
    </main>
  );
}

function OnboardingGate() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const accountApi = createAccountApi();
  const onboardingQuery = useQuery({
    enabled: isLoaded && isSignedIn,
    queryFn: () => accountApi.onboarding(),
    queryKey: ["account-onboarding"],
    retry: false,
    staleTime: 30_000,
  });

  if (!isLoaded || !isSignedIn) {
    return null;
  }

  if (onboardingQuery.isLoading) {
    return null;
  }

  if (onboardingQuery.error) {
    return null;
  }

  const onboarding = onboardingQuery.data?.onboarding ?? null;
  if (!onboarding || onboarding.stage === "completed") {
    return null;
  }

  const targetPath =
    onboarding.stage === "access" ? "/welcome/access" : "/welcome";
  return resolveOnboardingPath(location.pathname) === targetPath ? null : (
    <Navigate replace to={targetPath} />
  );
}

function ProductApp() {
  const location = useLocation();
  const isPublicRoute = isPublicMarketingRoute(location.pathname);
  const metaQuery = useQuery({
    queryKey: ["meta-congresses"],
    queryFn: () => api.meta(),
  });

  const currentCongress = metaQuery.data?.currentCongress;
  const showMetaLoading = metaQuery.isLoading && !isPublicRoute;

  return (
    <AppShell>
      {showMetaLoading ? (
        <RouteLoadingFallback label="Loading Congress metadata..." />
      ) : null}
      {!isPublicRoute ? <OnboardingGate /> : null}
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route
            path="/"
            element={<HomePage currentCongress={currentCongress} />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/*" element={<ResetPasswordPage />} />
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/welcome/access" element={<WelcomeAccessPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route
            path="/insights/federal-ai-legislation"
            element={<AiLegislationInsightPage />}
          />
          <Route
            path="/insights/congress-explained"
            element={<CongressExplainedPage />}
          />
          <Route
            path="/insights/how-a-bill-becomes-law"
            element={<HowABillBecomesLawPage />}
          />
          <Route
            path="/insights/what-is-cloture"
            element={<ClotureInsightPage />}
          />
          <Route
            path="/insights/why-bills-get-reintroduced"
            element={<ReintroducedBillsInsightPage />}
          />
          <Route
            path="/insights/reported-by-committee"
            element={<ReportedByCommitteeInsightPage />}
          />
          <Route
            path="/insights/money-and-influence"
            element={<MoneyInfluencePage />}
          />
          <Route
            path="/insights/outside-spending"
            element={<OutsideSpendingInsightPage />}
          />
          <Route
            path="/insights/pac-vs-super-pac"
            element={<PacVsSuperPacInsightPage />}
          />
          <Route path="/insights/:slug" element={<CongressExplainerPage />} />
          <Route
            path="/bills"
            element={<BillsPage currentCongress={currentCongress} />}
          />
          <Route
            path="/bills/:congress/:billType/:billNumber"
            element={<BillDetailPage />}
          />
          <Route
            path="/bills/:congress/:billType/:billNumber/votes/:voteId"
            element={<BillVoteDetailPage />}
          />
          <Route
            path="/members"
            element={<MembersPage currentCongress={currentCongress} />}
          />
          <Route path="/members/:bioguideId" element={<MemberDetailPage />} />
          <Route
            path="/fec/committees/:committeeId"
            element={<ProtectedFecCommitteeDetailPage />}
          />
          <Route
            path="/schedule"
            element={<SchedulePage currentCongress={currentCongress} />}
          />
          <Route
            path="/votes/house/:congress/:sessionNumber/:rollNumber"
            element={<HouseRollCallVoteDetailPage />}
          />
          <Route
            path="/votes/senate/:congress/:sessionNumber/:voteNumber"
            element={<SenateFloorVoteDetailPage />}
          />
          <Route
            path="/committees/:congress/:chamber/:committeeCode"
            element={<CommitteeDetailPage />}
          />
          <Route
            path="/workspace"
            element={<MonitorPage currentCongress={currentCongress} />}
          />
          <Route
            path="/workspace/monitors/new"
            element={<CreateMonitorPage />}
          />
          <Route
            path="/settings/account"
            element={
              <ProtectedSettingsPage>
                <AccountSettingsPage />
              </ProtectedSettingsPage>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedSettingsPage>
                <NotificationsSettingsPage />
              </ProtectedSettingsPage>
            }
          />
          <Route
            path="/schedule/:congress/:chamber/:eventId"
            element={<MeetingDetailPage />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export default function App() {
  const location = useLocation();

  if (isDesignSystemPreviewRoute(location.pathname)) {
    return isDesignSystemPreviewEnabled() ? (
      <Suspense
        fallback={<RouteLoadingFallback label="Loading design system..." />}
      >
        <DesignSystemPreviewPage />
      </Suspense>
    ) : (
      <DesignSystemPreviewUnavailablePage />
    );
  }

  return <ProductApp />;
}
